import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Input, PageHeader } from '../../packages/ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import './AISettingsPage.css';
import {
  getAICredential,
  setAICredential,
  deleteAICredential,
  type AICredentialStatus,
} from '../../packages/api-client/ai-credentials-api';

export function AISettingsPage() {
  usePageTitle('AI settings');
  const [status, setStatus] = useState<AICredentialStatus | null>(null);
  const [key, setKey] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () =>
    getAICredential()
      .then(setStatus)
      .catch(() => setStatus({ configured: false }));

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!key.trim()) return;
    setSaving(true);
    try {
      await setAICredential(key.trim());
      toast.success('Anthropic key saved');
      setKey('');
      await load();
    } catch {
      toast.error('That Anthropic key was rejected');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    await deleteAICredential();
    toast.success('Key removed — reverting to the free allowance');
    await load();
  };

  return (
    <section>
      <PageHeader
        title="AI / Anthropic key"
        subtitle="Add your own Anthropic API key to remove the free monthly AI limit."
      />
      <div className="card ai-settings-card">
        <h2 className="card-title">Anthropic connection</h2>
        <p className="ai-settings-description" id="ai-key-help">
          Your key is encrypted and never shown again after saving.
        </p>

        {status?.configured && status.credential && (
          <div className="ai-settings-current-key">
            <span>Current key: <code>••••{status.credential.last4}</code></span>
            <Button type="button" variant="secondary" size="sm" onClick={remove}>
              Remove
            </Button>
          </div>
        )}

        <form className="ai-settings-form" onSubmit={(event) => { event.preventDefault(); if (!saving) void save(); }}>
          <Input
            id="ai-key"
            label="Anthropic API key"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="sk-ant-..."
            autoComplete="off"
            aria-describedby="ai-key-help"
          />
          <Button type="submit" disabled={saving || !key.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </div>
    </section>
  );
}

export default AISettingsPage;
